/**
 * Consent banner, built on Shopify's Customer Privacy API.
 *
 * Three choices on the first layer, and Reject is exactly as easy as Accept: same
 * size, same row, one click each. That is not a style preference. UK PECR and the
 * ICO's position require rejecting non-essential cookies to be as easy as accepting,
 * and a banner that hides Reject behind Preferences is the pattern they wrote to UK
 * sites about in 2023 and 2024.
 *
 * Shopify's own banner must be OFF in Admin (Customer privacy > Cookie banner) or
 * both will render. That is a store setting and cannot be done from the theme.
 */
const FEATURE = { name: 'consent-tracking-api', version: '0.1' };

/**
 * How tall the banner is while it is up, published on the root element. The banner is fixed to
 * the foot of the screen, which is also where a funnel pins its own bar (the wine club quiz's
 * Start and Continue, the case's Subscribe). Those bars sit `var(--gv-consent-h, 0px)` above the
 * foot, so they stand on the banner instead of under it: measured on the takeover on 2026-09-30,
 * the banner covered the pinned button outright, 92px of it on a desktop and 204px on a phone.
 * It is `0px` whenever the banner is hidden, and unset until the banner has first been shown.
 */
const HEIGHT_PROPERTY = '--gv-consent-h';

class CookieBanner extends HTMLElement {
  #privacy = null;
  #lastFocus = null;
  /** Keeps the published height current: the text wraps, and the preferences view is taller. */
  #resize = new ResizeObserver(() => this.#publishHeight());

  connectedCallback() {
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

    window.Shopify.loadFeatures([FEATURE], (error) => {
      if (error) return; // no API, no banner: better silent than a banner wired to nothing
      this.#privacy = window.Shopify.customerPrivacy;
      if (!this.#privacy) return;

      this.#wire();

      // shouldShowBanner() is region aware, but on this store it stays TRUE after a
      // choice has been recorded, so it cannot be the only gate: measured on the real
      // storefront, a visitor who pressed Reject was asked again on the next page while
      // their rejection was still in force. The recorded consent is the reliable signal.
      if (this.#privacy.shouldShowBanner() && !this.#hasChoice()) this.#show();
    });
  }

  disconnectedCallback() {
    this.#resize.disconnect();
    document.documentElement.style.setProperty(HEIGHT_PROPERTY, '0px');
  }

  /** Any category answered either way means the visitor has already chosen. */
  #hasChoice() {
    let consent = {};
    try {
      consent = this.#privacy.currentVisitorConsent() || {};
    } catch (err) {
      return false;
    }
    return ['analytics', 'marketing', 'preferences', 'sale_of_data'].some(
      (key) => consent[key] === 'yes' || consent[key] === 'no'
    );
  }

  #wire() {
    this.querySelector('[data-accept]')?.addEventListener('click', () => this.#setAll(true));
    this.querySelector('[data-reject]')?.addEventListener('click', () => this.#setAll(false));
    this.querySelector('[data-prefs]')?.addEventListener('click', () => this.#openPrefs());
    this.querySelector('[data-prefs-cancel]')?.addEventListener('click', () => this.#closePrefs());
    this.querySelector('[data-prefs-save]')?.addEventListener('click', () => this.#savePrefs());
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dataset.view === 'prefs') this.#closePrefs();
    });

    // The way back in. Once a choice is recorded the banner never shows again, so without this a
    // visitor who accepted could not withdraw, and UK GDPR requires withdrawing to be as easy as
    // giving. Any link to #cookie-preferences reopens the panel: sections/footer-legal.liquid renders
    // one on every page with a footer, and the same href in a page's body (the privacy policy) works.
    document.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href="#cookie-preferences"]') : null;
      if (!link) return;
      event.preventDefault();
      this.#show();
      this.#openPrefs();
    });
  }

  #show() {
    this.hidden = false;
    this.dataset.view = 'banner';
    // Not a modal. It does not trap focus or block the page, because a consent
    // choice should not hold the site hostage while it is being made.
    this.#resize.observe(this);
    this.#publishHeight();
  }

  #hide() {
    this.hidden = true;
    this.dataset.view = 'banner';
    this.#resize.disconnect();
    this.#publishHeight();
  }

  #publishHeight() {
    // Rounded down. The banner is a fraction of a pixel tall (204.4px on a phone), and a bar
    // standing a whole pixel clear of it let a line of the page show between the two. A bar
    // tucked under the banner's top edge by that fraction shows nothing.
    const height = this.hidden ? 0 : Math.floor(this.getBoundingClientRect().height);
    document.documentElement.style.setProperty(HEIGHT_PROPERTY, `${height}px`);
  }

  #openPrefs() {
    // The opener explicitly, not document.activeElement. A pointer click leaves focus
    // on the body in some browsers, and returning focus to the body on Escape strands
    // a keyboard user at the top of the document.
    this.#lastFocus = this.querySelector('[data-prefs]');
    this.#prefill();
    this.dataset.view = 'prefs';
    this.querySelector('[data-prefs-panel] input')?.focus();
  }

  /**
   * Tick the boxes to match the choice already recorded, so reopening the panel from the footer
   * and saving without a change keeps what the visitor chose. Unprefilled, every box opened empty
   * and Save quietly withdrew whatever they had accepted. A visitor with no recorded choice keeps
   * whatever they have ticked so far.
   */
  #prefill() {
    if (!this.#hasChoice()) return;
    let consent = {};
    try {
      consent = this.#privacy.currentVisitorConsent() || {};
    } catch (err) {
      return;
    }
    for (const name of ['analytics', 'marketing', 'preferences']) {
      const box = this.querySelector(`[data-category="${name}"]`);
      if (box instanceof HTMLInputElement) box.checked = consent[name] === 'yes';
    }
  }

  #closePrefs() {
    this.dataset.view = 'banner';
    if (this.#lastFocus instanceof HTMLElement) this.#lastFocus.focus();
  }

  #apply(consent) {
    try {
      this.#privacy.setTrackingConsent(consent, () => this.#hide());
    } catch (err) {
      this.#hide(); // never leave the banner stuck open on an API error
    }
  }

  /**
   * The write goes to Shopify's Storefront API, so it fails with "Failed to fetch"
   * on a localhost dev server and succeeds on a real storefront domain. Verify this
   * banner on a theme preview URL, never on 127.0.0.1, or it will look broken while
   * being fine.
   */
  static get verificationNote() {
    return 'Verify on a storefront domain, not localhost.';
  }

  #setAll(value) {
    this.#apply({
      analytics: value,
      marketing: value,
      preferences: value,
      sale_of_data: value,
    });
  }

  #savePrefs() {
    const on = (name) => {
      const el = this.querySelector(`[data-category="${name}"]`);
      return el instanceof HTMLInputElement ? el.checked : false;
    };
    const marketing = on('marketing');
    this.#apply({
      analytics: on('analytics'),
      marketing,
      preferences: on('preferences'),
      // Shopify's sale_of_data is a CCPA concept with no UK equivalent. Tying it to
      // marketing keeps one honest answer rather than a fourth toggle nobody here
      // can interpret.
      sale_of_data: marketing,
    });
  }
}

if (!customElements.get('cookie-banner')) {
  customElements.define('cookie-banner', CookieBanner);
}
